import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Lock } from "lucide-react";
import api, { resolveAvatar } from "@/lib/api";

export default function HiredWorkers() {
    const [hired, setHired] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            const { data } = await api.get("/workers/hired");
            setHired(data.hired || []);
            setLoading(false);
        })();
    }, []);

    return (
        <div className="h-full overflow-y-auto wfos-scrollbar p-8">
            <div className="max-w-6xl">
                <div className="flex items-end justify-between">
                    <div>
                        <div className="wfos-label">Workforce · Your team</div>
                        <h1 className="mt-2 text-3xl font-medium tracking-tight text-gray-900">Your hired workforce</h1>
                        <p className="mt-1 text-sm text-gray-500">
                            {hired.length} Workforce Member{hired.length === 1 ? "" : "s"} on staff.
                        </p>
                    </div>
                    <Link
                        to="/app/workforce"
                        data-testid="hired-link-marketplace"
                        className="inline-flex items-center gap-1 text-sm font-medium text-[#16a34a] hover:text-[#15803d]"
                    >
                        Browse marketplace <ArrowRight className="w-3.5 h-3.5" strokeWidth={2} />
                    </Link>
                </div>

                {loading ? (
                    <div className="mt-10 text-sm text-gray-500">Loading…</div>
                ) : hired.length === 0 ? (
                    <div className="mt-10 wfos-card p-8 text-center">
                        <div className="text-lg font-medium text-gray-900">No hires yet</div>
                        <p className="mt-1 text-sm text-gray-500">
                            Activate your first Workforce Member in 60 seconds — try Aria the Content Writer.
                        </p>
                        <Link
                            to="/app/workforce"
                            className="mt-4 inline-flex items-center gap-1 rounded-lg bg-[#16a34a] px-3.5 py-2 text-sm font-medium text-white hover:bg-[#15803d]"
                        >
                            Open marketplace <ArrowRight className="w-3.5 h-3.5" strokeWidth={2} />
                        </Link>
                    </div>
                ) : (
                    <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                        {hired.map((h) => (
                            <Link
                                to={`/app/workers/${h.id}`}
                                key={h.id}
                                data-testid={`hired-card-${h.id}`}
                                className="wfos-card p-5 hover:border-[#d6d6cf] transition-colors flex items-start gap-4"
                            >
                                <img
                                    src={resolveAvatar(h.worker?.avatar_url)}
                                    alt={h.worker?.name}
                                    className="w-14 h-14 rounded-full object-cover border border-[#e8e8e3] flex-shrink-0"
                                />
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2">
                                        <div className="text-[15px] font-semibold text-gray-900 truncate">
                                            {h.custom_name || h.worker?.name}
                                        </div>
                                        {h.worker?.is_functional ? (
                                            <span className="wfos-pill wfos-pill--green">
                                                <Sparkles className="w-3 h-3" strokeWidth={2} /> Activated
                                            </span>
                                        ) : (
                                            <span className="wfos-pill">
                                                <Lock className="w-3 h-3" strokeWidth={2} /> Coming soon
                                            </span>
                                        )}
                                    </div>
                                    <div className="mt-1 text-[11px] font-semibold uppercase tracking-widest text-gray-500">
                                        {h.worker?.role}
                                    </div>
                                    <p className="mt-2 text-sm text-gray-600 leading-relaxed line-clamp-2">
                                        {h.worker?.tagline}
                                    </p>
                                    <div className="mt-3 text-xs text-gray-400">
                                        Hired {new Date(h.hired_at).toLocaleDateString()}
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
